import React from "react";
import { Box, Button, Card, Drawer, Stack, Text } from "braid-design-system";
import { getSystemSetup } from "../services/setupService";
import { getAllPaymentModes, PaymentModeResponse } from "../services/paymentModeService";
import {
    OtherPaymentAccountSuggestion,
    OtherPaymentsDetailLine,
    OtherPaymentsHistoryItem,
    OtherPaymentSaveRow,
    getOtherPaymentDocumentDetails,
    getRecentOtherPayments,
    saveOtherPayments,
    searchOtherPaymentAccounts,
} from "../services/otherPaymentsService";
import Loading from "../components/Loading";
import { useToast } from "../hooks/useToast";
import { sanitizeNumericString } from "../utils/numericInput";
import "../styles/OtherPayments.css";

type PaymentRow = {
    id: string;
    accountNo: string;
    accountName: string;
    payTo: string;
    description: string;
    amount: string;
};

const formatCurrency = (value: string): string => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return "0.00";
    }

    return parsed.toFixed(2);
};

const createRow = (index: number): PaymentRow => ({
    id: `other-payment-row-${index + 1}`,
    accountNo: "",
    accountName: "",
    payTo: "",
    description: "",
    amount: "0.00",
});

const normalizeLookupValue = (value: string): string => value.trim().toLowerCase();

type OtherPaymentsDrawerMode = "recent" | "details";

const OtherPayments: React.FC = () => {
    const toast = useToast();
    const [loading, setLoading] = React.useState(true);
    const [branchId, setBranchId] = React.useState<string>("");
    const [cashBook, setCashBook] = React.useState<PaymentModeResponse | null>(null);
    const [rows, setRows] = React.useState<PaymentRow[]>([createRow(0)]);
    const [accountSuggestions, setAccountSuggestions] = React.useState<Record<string, OtherPaymentAccountSuggestion[]>>({});
    const [activeSuggestionRowId, setActiveSuggestionRowId] = React.useState<string | null>(null);
    const [suggestionPopup, setSuggestionPopup] = React.useState<{ rowId: string; top: number; left: number; width: number } | null>(null);
    const inputRefs = React.useRef<Record<string, HTMLInputElement | null>>({});
    const suggestionRequestRef = React.useRef(0);
    const [saving, setSaving] = React.useState(false);
    const [recentPayments, setRecentPayments] = React.useState<OtherPaymentsHistoryItem[]>([]);
    const [recentPaymentsLoading, setRecentPaymentsLoading] = React.useState(false);
    const [showRecentPayments, setShowRecentPayments] = React.useState(false);
    const [drawerMode, setDrawerMode] = React.useState<OtherPaymentsDrawerMode>("recent");
    const [selectedPaymentDocNo, setSelectedPaymentDocNo] = React.useState<string | null>(null);
    const [selectedPaymentDetails, setSelectedPaymentDetails] = React.useState<OtherPaymentsDetailLine[]>([]);
    const [paymentDetailsLoading, setPaymentDetailsLoading] = React.useState(false);
    const latestPayment = React.useMemo(() => {
        if (!recentPayments.length) {
            return null;
        }

        return [...recentPayments].sort((left, right) => {
            const leftTime = left.created_at ? new Date(left.created_at).getTime() : 0;
            const rightTime = right.created_at ? new Date(right.created_at).getTime() : 0;

            if (rightTime !== leftTime) {
                return rightTime - leftTime;
            }

            return right.doc_no.localeCompare(left.doc_no, undefined, { numeric: true });
        })[0];
    }, [recentPayments]);

    React.useEffect(() => {
        let mounted = true;

        const loadPageData = async () => {
            try {
                setLoading(true);

                const [setup, paymentModes] = await Promise.all([
                    getSystemSetup().catch(() => null),
                    getAllPaymentModes().catch(() => []),
                ]);

                if (!mounted) {
                    return;
                }

                setBranchId(setup?.setup_storeid ?? "");
                setCashBook(paymentModes.find((mode) => mode.payIsCash) ?? null);
            } catch (error: any) {
                if (mounted) {
                    toast.showError(error?.message || "Failed to load other payments page data.");
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        loadPageData();

        return () => {
            mounted = false;
        };
    }, [toast]);

    const totalAmount = React.useMemo(
        () => rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
        [rows],
    );

    const updateRow = (rowId: string, field: keyof PaymentRow, value: string) => {
        setRows((currentRows) =>
            currentRows.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)),
        );

        if (field === "accountName") {
            const normalizedValue = normalizeLookupValue(value);
            setActiveSuggestionRowId(normalizedValue ? rowId : null);
            if (normalizedValue) {
                const inputElement = inputRefs.current[rowId];
                if (inputElement) {
                    const rect = inputElement.getBoundingClientRect();
                    setSuggestionPopup({
                        rowId,
                        top: rect.bottom + 4,
                        left: rect.left,
                        width: rect.width,
                    });
                }
            } else {
                setSuggestionPopup(null);
            }
            if (!normalizedValue) {
                setAccountSuggestions((currentSuggestions) => ({
                    ...currentSuggestions,
                    [rowId]: [],
                }));
            }
        }
    };

    const refreshSuggestionPopupPosition = React.useCallback((rowId: string) => {
        const inputElement = inputRefs.current[rowId];
        if (!inputElement) {
            return;
        }

        const rect = inputElement.getBoundingClientRect();
        setSuggestionPopup({
            rowId,
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
        });
    }, []);

    React.useEffect(() => {
        if (!activeSuggestionRowId) {
            setSuggestionPopup(null);
            return;
        }

        const activeRow = rows.find((row) => row.id === activeSuggestionRowId);
        const normalizedQuery = normalizeLookupValue(activeRow?.accountName ?? "");

        if (!normalizedQuery) {
            setSuggestionPopup(null);
            return;
        }

        const requestId = ++suggestionRequestRef.current;
        const timeoutId = window.setTimeout(() => {
            searchOtherPaymentAccounts(normalizedQuery)
                .then((suggestions) => {
                    if (suggestionRequestRef.current !== requestId) {
                        return;
                    }

                    setAccountSuggestions((currentSuggestions) => ({
                        ...currentSuggestions,
                        [activeSuggestionRowId]: suggestions,
                    }));
                    refreshSuggestionPopupPosition(activeSuggestionRowId);
                })
                .catch((error: any) => {
                    if (suggestionRequestRef.current !== requestId) {
                        return;
                    }

                    setAccountSuggestions((currentSuggestions) => ({
                        ...currentSuggestions,
                        [activeSuggestionRowId]: [],
                    }));
                    setSuggestionPopup(null);
                    toast.showError(error?.message || "Failed to load account suggestions.");
                });
        }, 250);

        const handleWindowChange = () => refreshSuggestionPopupPosition(activeSuggestionRowId);
        window.addEventListener("resize", handleWindowChange);
        window.addEventListener("scroll", handleWindowChange, true);

        return () => {
            window.clearTimeout(timeoutId);
            window.removeEventListener("resize", handleWindowChange);
            window.removeEventListener("scroll", handleWindowChange, true);
        };
    }, [activeSuggestionRowId, refreshSuggestionPopupPosition, rows, toast]);

    const addRow = () => {
        setRows((currentRows) => [...currentRows, createRow(currentRows.length)]);
    };

    const removeRow = (rowId: string) => {
        setRows((currentRows) => {
            if (currentRows.length === 1) {
                return currentRows;
            }

            const nextRows = currentRows.filter((row) => row.id !== rowId);
            setAccountSuggestions((currentSuggestions) => {
                const nextSuggestions = { ...currentSuggestions };
                delete nextSuggestions[rowId];
                return nextSuggestions;
            });

            if (activeSuggestionRowId === rowId) {
                setActiveSuggestionRowId(null);
                setSuggestionPopup(null);
            }

            return nextRows.length > 0 ? nextRows : [createRow(0)];
        });
    };

    const handleSelectAccountSuggestion = (rowId: string, suggestion: OtherPaymentAccountSuggestion) => {
        setRows((currentRows) =>
            currentRows.map((row) =>
                row.id === rowId
                    ? {
                          ...row,
                          accountName: suggestion.acc_name ?? "",
                          accountNo: suggestion.acc_code ?? "",
                      }
                    : row,
            ),
        );
        setActiveSuggestionRowId(null);
        setSuggestionPopup(null);
        setAccountSuggestions((currentSuggestions) => ({
            ...currentSuggestions,
            [rowId]: [],
        }));
    };

    const activeSuggestionRow = React.useMemo(
        () => rows.find((row) => row.id === activeSuggestionRowId) ?? null,
        [activeSuggestionRowId, rows],
    );
    const activeSuggestions = React.useMemo(
        () => (activeSuggestionRowId ? accountSuggestions[activeSuggestionRowId] || [] : []),
        [accountSuggestions, activeSuggestionRowId],
    );
    const showActiveSuggestions = Boolean(activeSuggestionRowId && suggestionPopup && activeSuggestions.length > 0 && activeSuggestionRow);

    const handleSendToApproval = async () => {
        const filledRows = rows.filter((row) => row.accountName.trim() || row.payTo.trim() || row.description.trim() || Number(row.amount) > 0);

        if (filledRows.length === 0) {
            toast.showError("Add at least one payment row before sending to approval.");
            return;
        }

        const payloadRows: OtherPaymentSaveRow[] = filledRows.map((row) => ({
            account_name: row.accountName.trim(),
            account_no: row.accountNo.trim() || undefined,
            pay_to: row.payTo.trim() || undefined,
            description: row.description.trim() || undefined,
            amount: row.amount,
        }));

        try {
            setSaving(true);
            const result = await saveOtherPayments(payloadRows);

            setRows([createRow(0)]);
            setAccountSuggestions({});
            setActiveSuggestionRowId(null);
            setSuggestionPopup(null);

            toast.showSuccess(`Saved ${result.inserted_count} payment row(s) as ${result.doc_no}.`);
                // refresh the latest payment shown in footer
                void loadRecentPayments(1);
        } catch (error: any) {
            toast.showError(error?.message || "Failed to save other payments.");
        } finally {
            setSaving(false);
        }
    };

    const loadRecentPayments = React.useCallback(async (limit = 10) => {
        try {
            setRecentPaymentsLoading(true);
            const data = await getRecentOtherPayments(limit);
            setRecentPayments(data);
            if (limit > 1) {
                setDrawerMode("recent");
                setSelectedPaymentDocNo(null);
                setSelectedPaymentDetails([]);
            }
            // only open the drawer when explicitly requested (limit > 1 indicates "list" mode)
            if (limit > 1) {
                setShowRecentPayments(true);
            }
        } catch (error: any) {
            toast.showError(error?.message || "Failed to load recent payments.");
        } finally {
            setRecentPaymentsLoading(false);
        }
    }, [toast]);

    const openRecentPayments = React.useCallback(() => {
        // load last 10 and open drawer
        void loadRecentPayments(10);
    }, [loadRecentPayments]);

    const openPaymentDetails = React.useCallback(
        async (docNo: string) => {
            const normalizedDocNo = docNo.trim();
            if (!normalizedDocNo) {
                toast.showError("No payment document selected.");
                return;
            }

            setDrawerMode("details");
            setSelectedPaymentDocNo(normalizedDocNo);
            setSelectedPaymentDetails([]);
            setShowRecentPayments(true);
            setPaymentDetailsLoading(true);

            try {
                const details = await getOtherPaymentDocumentDetails(normalizedDocNo);
                setSelectedPaymentDetails(details);
            } catch (error: any) {
                toast.showError(error?.message || "Failed to load payment details.");
            } finally {
                setPaymentDetailsLoading(false);
            }
        },
        [toast],
    );

    // Prefetch the latest payment on initial load so the footer always shows a value
    React.useEffect(() => {
        if (!loading) {
            void loadRecentPayments(1);
        }
    }, [loading, loadRecentPayments]);

    if (loading) {
        return <Loading message="Loading other payments..." />;
    }

    return (
        <Box className="other-payments-page">
            <Stack space="medium">
                <div className="other-payments-header-cards">
                    <div className="other-payments-header-card">
                        <div className="other-payments-info-card">
                            <Text size="small" tone="secondary" weight="strong">
                                Branch
                            </Text>
                            <Text size="standard" weight="strong">
                                {branchId || "Not configured"}
                            </Text>
                        </div>
                    </div>

                    <div className="other-payments-header-card">
                        <div className="other-payments-info-card">
                            <Text size="small" tone="secondary" weight="strong">
                                Cash book
                            </Text>
                            <Text size="standard" weight="strong">
                                {cashBook?.payAccNo || "Not configured"}
                            </Text>
                        </div>
                    </div>
                </div>

                <Card>
                    <Box padding="small">
                        <Stack space="small">
                            <Text weight="strong" size="standard">
                                Payments
                            </Text>

                            <div className="other-payments-table-wrap">
                                <table className="other-payments-table">
                                    <thead>
                                        <tr>
                                            <th>Account No</th>
                                            <th>Account Name</th>
                                            <th>Pay To</th>
                                            <th>Description</th>
                                            <th className="other-payments-amount-column">Amount</th>
                                            <th className="other-payments-action-column">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, index) => {
                                            const isLastRow = index === rows.length - 1;

                                            return (
                                                <tr key={row.id}>
                                                    <td>
                                                        <div className="other-payments-account-cell">
                                                            <div className="other-payments-account-code">{row.accountNo || "-"}</div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="other-payments-autocomplete-cell">
                                                            <input
                                                                ref={(element) => {
                                                                    inputRefs.current[row.id] = element;
                                                                }}
                                                                value={row.accountName}
                                                                onChange={(event) => {
                                                                    updateRow(row.id, "accountName", event.target.value);
                                                                    refreshSuggestionPopupPosition(row.id);
                                                                }}
                                                                onFocus={() => {
                                                                    setActiveSuggestionRowId(row.id);
                                                                    refreshSuggestionPopupPosition(row.id);
                                                                }}
                                                                onBlur={() => {
                                                                    window.setTimeout(() => {
                                                                        if (activeSuggestionRowId === row.id) {
                                                                            setActiveSuggestionRowId(null);
                                                                            setSuggestionPopup(null);
                                                                        }
                                                                    }, 150);
                                                                }}
                                                                placeholder="Type account name..."
                                                                className="other-payments-input"
                                                            />
                                                            {showActiveSuggestions && suggestionPopup?.rowId === row.id ? (
                                                                <div className="other-payments-suggestion-list">
                                                                    {activeSuggestions.map((suggestion) => (
                                                                        <button
                                                                            key={`${suggestion.acc_code ?? ""}-${suggestion.acc_name ?? ""}`}
                                                                            type="button"
                                                                            className="other-payments-suggestion-item"
                                                                            onMouseDown={(event) => event.preventDefault()}
                                                                            onClick={() => handleSelectAccountSuggestion(row.id, suggestion)}
                                                                        >
                                                                            <span className="other-payments-suggestion-name">{suggestion.acc_name || "Unnamed account"}</span>
                                                                            <span className="other-payments-suggestion-code">{suggestion.acc_code || ""}</span>
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <input
                                                            value={row.payTo}
                                                            onChange={(event) => updateRow(row.id, "payTo", event.target.value)}
                                                            placeholder="Payee Name"
                                                            className="other-payments-input"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            value={row.description}
                                                            onChange={(event) => updateRow(row.id, "description", event.target.value)}
                                                            placeholder="Description"
                                                            className="other-payments-input"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            value={row.amount}
                                                            onChange={(event) => updateRow(row.id, "amount", sanitizeNumericString(event.target.value))}
                                                            placeholder="0.00"
                                                            inputMode="decimal"
                                                            className="other-payments-input other-payments-amount-input"
                                                        />
                                                    </td>
                                                    <td className="other-payments-action-cell">
                                                        {isLastRow ? (
                                                            <button
                                                                type="button"
                                                                className="other-payments-row-button other-payments-row-button-add"
                                                                onClick={addRow}
                                                                aria-label="Add payment row"
                                                            >
                                                                +
                                                            </button>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="other-payments-row-button other-payments-row-button-remove"
                                                                onClick={() => removeRow(row.id)}
                                                                aria-label="Remove payment row"
                                                                disabled={rows.length === 1}
                                                            >
                                                                -
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        <tr className="other-payments-total-row">
                                            <td />
                                            <td />
                                            <td />
                                            <td className="other-payments-total-label">Total</td>
                                            <td className="other-payments-total-value">{formatCurrency(String(totalAmount))}</td>
                                            <td />
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="other-payments-actions">
                                <div className="other-payments-save-button">
                                    <Button
                                        size="small"
                                        tone="brandAccent"
                                        variant="solid"
                                        onClick={() => {
                                            if (!saving) {
                                                void handleSendToApproval();
                                            }
                                        }}
                                    >
                                        {saving ? "Saving..." : "Save"}
                                    </Button>
                                </div>
                            </div>
                        </Stack>
                    </Box>
                </Card>

                <div className="other-payments-last-payment-line">
                    <button
                        type="button"
                        className="other-payments-last-payment-link"
                        onClick={latestPayment ? () => void openPaymentDetails(latestPayment.doc_no) : undefined}
                    >
                        {latestPayment ? (
                            <>
                                Last payment: {latestPayment.doc_no} ({latestPayment.line_count} line(s)) {formatCurrency(String(latestPayment.total_amount))} on {latestPayment.created_at ? new Date(latestPayment.created_at).toLocaleString() : "-"}
                            </>
                        ) : (
                            <>Last payment: No payments submitted yet</>
                        )}
                    </button>
                    <span className="other-payments-arrow" aria-hidden="true">
                        -&gt;
                    </span>
                    <button type="button" className="other-payments-last-view-link" onClick={openRecentPayments}>
                        list last 10
                    </button>
                </div>

                <Drawer
                    title={drawerMode === "details" && selectedPaymentDocNo ? `Other Payments Details - ${selectedPaymentDocNo}` : "Other Payments List View"}
                    open={showRecentPayments}
                    onClose={() => setShowRecentPayments(false)}
                    width="large"
                >
                    <div className="other-payments-history-drawer">
                        <div className="other-payments-history-header">
                            <Text tone="secondary">
                                {drawerMode === "details"
                                    ? selectedPaymentDocNo
                                        ? `Detailed lines for ${selectedPaymentDocNo}`
                                        : "Payment details"
                                    : "Recent other payment documents"}
                            </Text>
                        </div>

                        {drawerMode === "details" ? paymentDetailsLoading ? (
                            <Text size="small" tone="secondary">
                                Loading payment details...
                            </Text>
                        ) : selectedPaymentDetails.length > 0 ? (
                            <div className="other-payments-history-table-wrap">
                                <table className="other-payments-history-table">
                                    <thead>
                                        <tr>
                                            <th>No</th>
                                            <th>Account No</th>
                                            <th>Account Name</th>
                                            <th>Pay To</th>
                                            <th>Description</th>
                                            <th>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedPaymentDetails.map((line, index) => (
                                            <tr key={line.id}>
                                                <td>{index + 1}</td>
                                                <td>{line.account_no || "-"}</td>
                                                <td>{line.account_name || "-"}</td>
                                                <td>{line.pay_to || "-"}</td>
                                                <td>{line.description || "-"}</td>
                                                <td>{formatCurrency(String(line.amount))}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <Text size="small" tone="secondary">
                                No payment details found.
                            </Text>
                        ) : recentPaymentsLoading ? (
                            <Text size="small" tone="secondary">
                                Loading recent payments...
                            </Text>
                        ) : recentPayments.length > 0 ? (
                            <div className="other-payments-history-table-wrap">
                                <table className="other-payments-history-table">
                                    <thead>
                                        <tr>
                                            <th>Payment Number</th>
                                            <th>Line Count</th>
                                            <th>Amount</th>
                                            <th>Payment Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[...recentPayments]
                                            .sort((left, right) => {
                                                const leftTime = left.created_at ? new Date(left.created_at).getTime() : 0;
                                                const rightTime = right.created_at ? new Date(right.created_at).getTime() : 0;

                                                if (rightTime !== leftTime) {
                                                    return rightTime - leftTime;
                                                }

                                                return right.doc_no.localeCompare(left.doc_no, undefined, { numeric: true });
                                            })
                                            .map((payment) => (
                                                <tr key={payment.doc_no}>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="other-payments-history-table-link"
                                                            onClick={() => void openPaymentDetails(payment.doc_no)}
                                                        >
                                                            {payment.doc_no}
                                                        </button>
                                                    </td>
                                                    <td>{payment.line_count}</td>
                                                    <td>{formatCurrency(String(payment.total_amount))}</td>
                                                    <td>{payment.created_at ? new Date(payment.created_at).toLocaleString() : "-"}</td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <Text size="small" tone="secondary">
                                No recent payments found.
                            </Text>
                        )}
                    </div>
                </Drawer>

            </Stack>
        </Box>
    );
};

export default OtherPayments;